<template>
	<v-card class="password-guidance-card mt-2" outlined flat>
		<div class="password-guidance-card__header">
			<div class="d-flex align-center flex-wrap">
				<v-icon small color="primary" class="mr-2">fas fa-shield-alt</v-icon>
				<span class="password-guidance-card__title mr-2">Password requirements</span>
				<v-spacer />
				<span v-if="password" class="password-guidance-card__count mr-2">
					{{ metCount }}/{{ requirements.length }}
				</span>
				<v-chip
					v-if="password"
					x-small
					:color="strengthChipColor"
					:text-color="strengthChipTextColor"
					class="font-weight-medium"
				>
					{{ strengthLabel }}
				</v-chip>
			</div>
			<v-progress-linear
				:value="progressPercent"
				:color="progressColor"
				background-color="rgba(30, 124, 131, 0.1)"
				height="4"
				rounded
				class="mt-2 mb-0"
			/>
		</div>

		<v-row dense class="password-guidance-grid px-2 pb-2 pt-1">
			<v-col
				v-for="(req, index) in requirements"
				:key="index"
				cols="12"
				sm="6"
				class="py-0"
			>
				<div
					class="password-guidance-grid__item"
					:class="itemStateClass(req)"
				>
					<v-icon
						x-small
						class="password-guidance-grid__icon flex-shrink-0"
						:color="statusIconColor(req)"
					>
						{{ statusIcon(req) }}
					</v-icon>
					<span class="password-guidance-grid__text">{{ req }}</span>
				</div>
			</v-col>
		</v-row>

		<v-expand-transition>
			<div v-if="rejectionErrors && rejectionErrors.length" class="password-guidance-card__errors px-3 pb-2 pt-0">
				<v-divider class="mb-2" />
				<div class="d-flex align-start">
					<v-icon x-small color="error" class="mr-1 mt-1">fas fa-exclamation-circle</v-icon>
					<ul class="password-guidance-card__error-list mb-0 pl-0">
						<li v-for="(err, i) in rejectionErrors" :key="'rej-' + i">{{ err }}</li>
					</ul>
				</div>
			</div>
		</v-expand-transition>
	</v-card>
</template>

<script>
import { PASSWORD_REQUIREMENTS, isRequirementMet } from "../utils/passwordPolicy"

const REQUIREMENT_ICONS = [
	"fas fa-arrows-alt-h",
	"fas fa-compress-alt",
	"fas fa-arrow-up",
	"fas fa-arrow-down",
	"fas fa-hashtag",
	"fas fa-asterisk",
	"fas fa-shield-alt",
	"fas fa-ban",
]

export default {
	name: "PasswordGuidance",
	props: {
		password: { type: String, default: "" },
		rejectionErrors: { type: Array, default: () => [] },
		dense: { type: Boolean, default: true },
	},
	data() {
		return { requirements: PASSWORD_REQUIREMENTS }
	},
	computed: {
		metCount() {
			if (!this.password) return 0
			return this.requirements.filter((req) => isRequirementMet(req, this.password) === true).length
		},
		allMet() {
			return this.password && this.metCount === this.requirements.length
		},
		progressPercent() {
			if (!this.password) return 0
			return Math.round((this.metCount / this.requirements.length) * 100)
		},
		progressColor() {
			if (!this.password) return "grey lighten-1"
			if (this.allMet) return "success"
			if (this.metCount >= this.requirements.length / 2) return "primary"
			return "warning"
		},
		strengthLabel() {
			if (this.allMet) return "Strong"
			if (this.metCount >= 6) return "Good"
			if (this.metCount >= 4) return "Fair"
			return "Weak"
		},
		strengthChipColor() {
			if (this.allMet) return "success"
			if (this.metCount >= 6) return "primary lighten-4"
			if (this.metCount >= 4) return "warning lighten-4"
			return "error lighten-4"
		},
		strengthChipTextColor() {
			if (this.allMet) return "white"
			if (this.metCount >= 6) return "primary darken-2"
			if (this.metCount >= 4) return "warning darken-2"
			return "error darken-2"
		},
	},
	methods: {
		requirementIcon(index) {
			return REQUIREMENT_ICONS[index] || "fas fa-circle"
		},
		checkState(req) {
			if (!this.password) return "pending"
			const ok = isRequirementMet(req, this.password)
			if (ok === true) return "met"
			if (ok === false) return "unmet"
			return "pending"
		},
		itemStateClass(req) {
			return `password-guidance-grid__item--${this.checkState(req)}`
		},
		statusIconColor(req) {
			const state = this.checkState(req)
			if (state === "met") return "success"
			if (state === "unmet") return "error"
			return "grey"
		},
		statusIcon(req) {
			const index = this.requirements.indexOf(req)
			if (this.checkState(req) === "met") return "fas fa-check"
			if (this.checkState(req) === "unmet") return "fas fa-times"
			return this.requirementIcon(index)
		},
	},
}
</script>

<style scoped>
.password-guidance-card {
	border-radius: 8px !important;
	border: 1px solid rgba(30, 124, 131, 0.18) !important;
	background: #f9fdfd !important;
}

.password-guidance-card__header {
	padding: 10px 12px 8px;
}

.password-guidance-card__title {
	font-size: 0.875rem;
	font-weight: 600;
	color: #1a5c62;
}

.password-guidance-card__count {
	font-size: 0.75rem;
	color: rgba(0, 0, 0, 0.5);
}

.password-guidance-grid__item {
	display: flex;
	align-items: flex-start;
	gap: 6px;
	padding: 4px 6px;
	border-radius: 6px;
	min-height: 28px;
	line-height: 1.3;
}

.password-guidance-grid__item--met {
	background: rgba(76, 175, 80, 0.1);
}

.password-guidance-grid__item--unmet {
	background: rgba(244, 67, 54, 0.08);
}

.password-guidance-grid__text {
	font-size: 0.75rem;
	color: rgba(0, 0, 0, 0.75);
}

.password-guidance-grid__item--met .password-guidance-grid__text {
	color: #2e7d32;
}

.password-guidance-grid__item--unmet .password-guidance-grid__text {
	color: #c62828;
}

.password-guidance-grid__icon {
	margin-top: 2px;
}

.password-guidance-card__errors {
	background: rgba(244, 67, 54, 0.05);
}

.password-guidance-card__error-list {
	list-style: none;
	font-size: 0.75rem;
	line-height: 1.35;
	color: #c62828;
}

.password-guidance-card__error-list li {
	margin-bottom: 2px;
}

.password-guidance-card__error-list li:last-child {
	margin-bottom: 0;
}
</style>
